import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '..';
import type { SimulationChatState, SimulationAttachment, SimulationChatMessage } from './types';

import { nanoid } from 'nanoid';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { apolloClient } from 'src/lib/apollo-client';

import { GENERATE_SIMULATION_MUTATION } from './graphql';

const initialState: SimulationChatState = {
  messages: [],
  isSending: false,
  error: null,
  currentHtml: null,
};

interface SendMessagePayload {
  content: string;
  attachments?: Array<{
    id: string;
    name: string;
    size: number;
    type: string;
    base64Data: string;
    preview?: string;
  }>;
}

interface GraphQLSimulationMessage {
  id: string;
  role: string;
  content: string;
  htmlContent: string | null;
  attachments: Array<{ name: string; type: string }> | null;
  createdAt: string;
}

export const sendSimulationMessage = createAsyncThunk<
  { message: SimulationChatMessage },
  SendMessagePayload,
  { state: RootState }
>('simulationChat/sendMessage', async ({ content, attachments }, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const existingMessages = state.simulationChat.messages;

    // Build the messages array for the API including history
    const messagesForApi = existingMessages.map((msg) => ({
      role: msg.role,
      content: msg.content,
      attachments: msg.attachments?.map((att) => ({
        name: att.name,
        type: att.type,
        base64Data: att.base64Data,
      })),
    }));

    // Add the new message
    messagesForApi.push({
      role: 'user',
      content,
      attachments: attachments?.map((att) => ({
        name: att.name,
        type: att.type,
        base64Data: att.base64Data,
      })),
    });

    const { data } = await apolloClient.mutate<{
      generateSimulation: {
        message: GraphQLSimulationMessage;
      };
    }>({
      mutation: GENERATE_SIMULATION_MUTATION,
      variables: {
        input: {
          messages: messagesForApi,
        },
      },
    });

    const result = data?.generateSimulation;
    if (!result) {
      throw new Error('No response from server');
    }

    // Convert GraphQL response to our type
    const responseMessage: SimulationChatMessage = {
      id: result.message.id,
      role: result.message.role as 'user' | 'assistant',
      content: result.message.content,
      htmlContent: result.message.htmlContent,
      attachments: result.message.attachments?.map((att) => ({
        id: nanoid(),
        name: att.name,
        size: 0,
        type: att.type,
        base64Data: '',
      })),
      createdAt: result.message.createdAt,
    };

    return {
      message: responseMessage,
    };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to generate simulation');
  }
});

const simulationChatSlice = createSlice({
  name: 'simulationChat',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.messages = [];
      state.error = null;
      state.currentHtml = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setCurrentHtml: (state, action: PayloadAction<string | null>) => {
      state.currentHtml = action.payload;
    },
    addUserMessage: (
      state,
      action: PayloadAction<{ content: string; attachments?: SimulationAttachment[] }>
    ) => {
      const userMessage: SimulationChatMessage = {
        id: nanoid(),
        role: 'user',
        content: action.payload.content,
        attachments: action.payload.attachments,
        createdAt: new Date().toISOString(),
      };
      state.messages.push(userMessage);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendSimulationMessage.pending, (state) => {
        state.isSending = true;
        state.error = null;
      })
      .addCase(sendSimulationMessage.fulfilled, (state, action) => {
        state.isSending = false;
        state.messages.push(action.payload.message);

        // Update current HTML if the response contains HTML content
        if (action.payload.message.htmlContent) {
          state.currentHtml = action.payload.message.htmlContent;
        }
      })
      .addCase(sendSimulationMessage.rejected, (state, action) => {
        state.isSending = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearMessages, clearError, setCurrentHtml, addUserMessage } =
  simulationChatSlice.actions;

// Selectors
export const selectSimulationMessages = (state: RootState) => state.simulationChat.messages;
export const selectSimulationIsSending = (state: RootState) => state.simulationChat.isSending;
export const selectSimulationError = (state: RootState) => state.simulationChat.error;
export const selectCurrentHtml = (state: RootState) => state.simulationChat.currentHtml;

export default simulationChatSlice.reducer;
